import {CommonModule} from '@angular/common';
import {
    CheckboxGroupComponent,
    DatePipe,
    LoadComponent,
    NumberPipe,
    PaginationComponent,
    TrueFalseComponent,
} from '@brunata-angular/ui';
import {
    comparator,
    composeComparators,
    filterNulls,
    fromControlWithDebounceTime,
    HiddenValueWhileLoading,
    includesIgnoreCase,
    numericLocaleComparator,
    switchMapWithLoading,
    TranslatePipe,
    TranslateService,
} from '@brunata-angular/util';
import {ChangeDetectionStrategy, Component, inject, OnDestroy, viewChildren} from '@angular/core';
import {FilterData, MeterService, SelectedColumns} from '../meter.service';
import {applySortingToHeaders, SortableTableHeaderComponent, Sorting} from '@brunata-angular/ui';

import {NgbDropdown, NgbDropdownMenu, NgbDropdownToggle} from '@ng-bootstrap/ng-bootstrap';
import {FormBuilder, FormControl, ReactiveFormsModule} from '@angular/forms';
import {LocationMeter} from './location-meter.model';
import {InputTextGroupComponent} from '@brunata-angular/ui';
import {InputDropdownCheckboxesComponent, SelectableItem} from '@brunata-angular/ui';
import {InputDateGroupComponent} from '@brunata-angular/ui';
import {DateTime} from 'luxon';
import {BehaviorSubject, combineLatest, filter, map, Observable, startWith, take, tap} from 'rxjs';
import {ActivatedRoute, NavigationEnd, Router, RouterLink, RouterOutlet} from '@angular/router';
import {Store} from '@ngrx/store';
import {selectBuilding} from 'src/app/state/selectors';
import {InputTypeaheadComponent} from '@brunata-angular/ui';
import {uniqBy} from 'lodash-es';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';

@Component({
    selector: 'app-meter-list',
    standalone: true,
    imports: [
        SortableTableHeaderComponent,
        CommonModule,
        LoadComponent,
        ReactiveFormsModule,
        TrueFalseComponent,
        RouterLink,
        PaginationComponent,
        CheckboxGroupComponent,
        InputTextGroupComponent,
        DatePipe,
        TranslatePipe,
        NgbDropdown,
        NgbDropdownToggle,
        NgbDropdownMenu,
        CheckboxGroupComponent,
        InputTextGroupComponent,
        InputDropdownCheckboxesComponent,
        InputDateGroupComponent,
        InputTypeaheadComponent,
        PaginationComponent,
        RouterOutlet,
        NumberPipe,
    ],
    templateUrl: './meter-list.component.html',
    styleUrl: './meter-list.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MeterListComponent implements OnDestroy {
    private readonly headers = viewChildren(SortableTableHeaderComponent);

    private readonly store = inject(Store);
    private readonly router = inject(Router);
    private readonly activatedRoute = inject(ActivatedRoute);
    public readonly meterService = inject(MeterService);
    readonly selectedColumnsForm = this.formBuilder.group({
        proBraLoc: [{value: true, disabled: true}],
        address: [{value: true, disabled: true}],
        meterSuperTypeNo: [{value: true, disabled: true}],
        alloUnitType: [{value: true, disabled: true}],
        serialNo: [{value: true, disabled: true}],
        meterModel: [{value: true, disabled: true}],
        scale: [false],
        unitFactor: [false],
        mountingDate: [false],
        roomId: [false],
        meterBuildLength: [false],
        meterConnectionType: [false],
        shutoffValve: [false],
        supplyOrReturnFlow: [false],
        dinRail: [false],
        maxCurrent: [false],
        sensorPocket: [false],
        meterSize: [false],
        meterConnectionTypeId: [false],
        meterSequenceNo: [false],
        radioTechnology: [false],
    });

    readonly formGroupFilter = this.formBuilder.group({
        proBraLoc: new FormControl<string | null>(''),
        address: new FormControl<string | null>(''),
        meterSuperTypeNo: new FormControl<SelectableItem[]>([]),
        alloUnitType: new FormControl<SelectableItem[]>([]),
        serialNo: new FormControl<string | null>(''),
        meterModel: new FormControl<string | null>(''),
        scale: new FormControl<string | null>(''),
        unitFactor: new FormControl<string | null>(''),
        mountingDate: new FormControl<DateTime | null>(null),
        dismountedDate: new FormControl<string | null>(''),
        roomId: new FormControl<string | null>(''),
        meterBuildLength: new FormControl<SelectableItem[]>([]),
        meterConnectionType: new FormControl<SelectableItem[]>([]),
        shutoffValve: new FormControl<SelectableItem[]>([]),
        supplyOrReturnFlow: new FormControl<SelectableItem[]>([]),
        dinRail: new FormControl<SelectableItem[]>([]),
        maxCurrent: new FormControl<SelectableItem[]>([]),
        sensorPocket: new FormControl<SelectableItem[]>([]),
        meterSize: new FormControl<SelectableItem[]>([]),
        meterConnectionTypeId: new FormControl<string | null>(''),
        meterSequenceNo: new FormControl<string | null>(''),
        radioTechnology: new FormControl<SelectableItem[]>([]),
    });

    readonly noActivatedChildren$: Observable<boolean> = this.router.events.pipe(
        filter(event => event instanceof NavigationEnd),
        startWith(null),
        map(() => this.activatedRoute.children.length === 0),
    );

    readonly tableSettings$: Observable<any> = combineLatest([
        this.meterService.selectedColumns,
        this.meterService.meterListFilterSubject,
    ]).pipe(
        take(1),
        tap(([selectedColumns, filterValue]) => {
            this.selectedColumnsForm.patchValue(selectedColumns);
            this.formGroupFilter.patchValue(filterValue ?? {}, {emitEvent: false});
        }),
    );

    readonly view$: Observable<
        HiddenValueWhileLoading<{
            meters: LocationMeter[];
            collectionSize: number;
            meterModels: Map<string, string>;
            buildingNo: string;
        }>
    > = this.store.select(selectBuilding).pipe(
        map(buildingState =>
            buildingState.status === 'success' ? buildingState.buildingNo : null,
        ),
        filterNulls(),
        switchMapWithLoading(buildingNo =>
            combineLatest([
                this.meterService
                    .getPhysicalMeters(buildingNo)
                    .pipe(
                        tap(locationMeters =>
                            this.updateFilterOptions(
                                locationMeters,
                                this.meterService.meterListFilterSubject,
                            ),
                        ),
                    ),

                fromControlWithDebounceTime(this.formGroupFilter).pipe(
                    map(() => this.formGroupFilter.getRawValue()),
                ),
                this.meterService.sorting,
                this.tableSettings$,
            ]).pipe(
                map(([locationMeters, formGroupFilter, sorting]) => {
                    const filteredMeters = this.filterLocationMeters(
                        locationMeters,
                        formGroupFilter,
                    );
                    return {
                        meters: this.sortMeters(filteredMeters, sorting),
                        collectionSize: filteredMeters.length,
                        meterModels: this.getUniqueValuesForMeterModel(
                            locationMeters,
                            'meterModel',
                        ),
                        buildingNo: buildingNo,
                        sorting: sorting,
                    };
                }),
                tap(data => {
                    setTimeout(() => {
                        applySortingToHeaders(this.headers(), data.sorting);
                    }, 0);
                }),
            ),
        ),
    );

    constructor(
        private translateService: TranslateService,
        private formBuilder: FormBuilder,
    ) {
        this.formGroupFilter.valueChanges.pipe(takeUntilDestroyed()).subscribe(() => {
            this.meterService.page.set(1);
        });
    }

    ngOnDestroy(): void {
        this.saveFilter();
    }

    getUniqueValuesForMeterModel(
        locationMeters: LocationMeter[],
        key: keyof LocationMeter,
    ): Map<string, string> {
        const meterModelMap = new Map<string, string>();
        if (key === 'meterModel') {
            locationMeters.forEach(locationMeter => {
                if (locationMeter.meterModel != null) {
                    meterModelMap.set(locationMeter.meterModel, locationMeter.meterModel);
                }
            });
        }
        return meterModelMap;
    }

    getUniqueValuesForLists(
        locationMeters: LocationMeter[],
        key: keyof LocationMeter,
    ): SelectableItem[] {
        const valueList = locationMeters.map(locationMeter => {
            let name = locationMeter[key]?.toString() ?? '';
            switch (key) {
                case 'meterSuperTypeNo':
                    name = this.translateService.translate(
                        'mappers_meterType.' + locationMeter.meterSuperTypeNo,
                        'online',
                        'common',
                    );
                    break;
                case 'alloUnitType':
                    name = this.translateService.translate(
                        'mappers_allocationUnitMap.' + locationMeter.alloUnitType,
                        'online',
                        'common',
                    );
                    break;
                case 'radioTechnology':
                    name = this.translateService.translate(
                        'radiotechnology_' + locationMeter.radioTechnology,
                    );
                    break;
                case 'sensorPocket':
                    switch (locationMeter.sensorPocket) {
                        case true:
                            name = this.translateService.translate(
                                'actions_yes',
                                'online',
                                'common',
                            );
                            break;
                        case false:
                            name = this.translateService.translate(
                                'actions_no',
                                'online',
                                'common',
                            );
                            break;
                        case null:
                            name = this.translateService.translate(
                                'actions_unknown',
                                'online',
                                'common',
                            );
                            break;
                    }
                    break;
                case 'shutoffValve':
                    switch (locationMeter.shutoffValve) {
                        case 'A':
                            name = this.translateService.translate('utilities_.AFTER');
                            break;
                        case 'B':
                            name = this.translateService.translate('utilities_.BEFORE');
                            break;
                        case 'M':
                            name = this.translateService.translate('utilities_.BOTH');
                            break;
                        case 'N':
                            name = this.translateService.translate('utilities_.NONE');
                            break;
                    }
                    break;
                case 'supplyOrReturnFlow':
                    switch (locationMeter.supplyOrReturnFlow) {
                        case 'C':
                            name = this.translateService.translate('utilities_.COLD');
                            break;
                        case 'W':
                            name = this.translateService.translate('utilities_.WARM');
                            break;
                        case '':
                            name = this.translateService.translate('common_NO_VALUE');
                            break;
                    }
                    break;
                case 'dinRail':
                    switch (locationMeter.dinRail) {
                        case true:
                            name = this.translateService.translate(
                                'actions_yes',
                                'online',
                                'common',
                            );
                            break;
                        case false:
                            name = this.translateService.translate(
                                'actions_no',
                                'online',
                                'common',
                            );
                            break;
                        case null:
                            name = this.translateService.translate(
                                'actions_unknown',
                                'online',
                                'common',
                            );
                            break;
                    }
                    break;
            }

            return {
                name:
                    locationMeter[key] === null
                        ? this.translateService.translate('common_NO_VALUE')
                        : name,
                selected: false,
                sortOrder: locationMeter[key] === null ? 1 : 0,
            };
        });
        valueList.sort(
            composeComparators([
                comparator({
                    selector: meter => meter.sortOrder,
                    order: 'asc',
                }),
                comparator({
                    selector: meter => meter.name,
                    order: 'asc',
                }),
            ]),
        );
        return uniqBy(valueList, item => item.name);
    }

    filterDate(
        locationMeter: LocationMeter,
        filterValue: DateTime | null,
        key: keyof LocationMeter,
    ): boolean {
        const date = locationMeter[key] instanceof DateTime ? locationMeter[key] : null;
        if (filterValue === null) {
            return true;
        }
        if (date != null && date instanceof DateTime) {
            return (
                date.day === filterValue.day &&
                date.month === filterValue.month &&
                date.year === filterValue.year
            );
        }
        return false;
    }

    filterLocationMeterValuesFilterList(
        value: string | null,
        filterValue: SelectableItem[] | null,
    ): boolean {
        const valuesToFilter = filterValue?.filter(it => it.selected).map(it => it.name);
        if (valuesToFilter?.length === 0) {
            return true;
        }
        if (value === null) {
            return filterValue?.some(it => it.selected && it.name === '') ?? true;
        }
        return filterValue?.some(it => it.selected && it.name === value) ?? true;
    }

    filterLocationMeters(locationMeters: LocationMeter[], filterData: FilterData): LocationMeter[] {
        return locationMeters.filter(
            locationMeter =>
                ((locationMeter.propertyNo == null &&
                    locationMeter.branchNo == null &&
                    locationMeter.locationNo == null &&
                    filterData.proBraLoc != null &&
                    filterData.proBraLoc === '') ||
                    (
                        locationMeter.propertyNo +
                        '-' +
                        locationMeter.branchNo +
                        '-' +
                        locationMeter.locationNo
                    )
                        .toLowerCase()
                        .includes((filterData.proBraLoc ?? '').toLowerCase())) &&
                (filterData.address === '' ||
                    (locationMeter.address != null &&
                        locationMeter.address
                            .toLowerCase()
                            .includes((filterData.address ?? '').toLowerCase()))) &&
                this.filterLocationMeterValuesFilterList(
                    this.translateService.translate(
                        'mappers_allocationUnitMap.' + locationMeter.alloUnitType,
                        'online',
                        'common',
                    ),
                    filterData.alloUnitType,
                ) &&
                includesIgnoreCase(locationMeter.serialNo, filterData.serialNo) &&
                includesIgnoreCase(locationMeter.meterModel, filterData.meterModel) &&
                (filterData.scale === '' ||
                    (locationMeter.scale != null &&
                        locationMeter.scale.toString().includes(filterData.scale ?? ''))) &&
                includesIgnoreCase(locationMeter.roomId, filterData.roomId) &&
                (filterData.unitFactor === '' ||
                    (locationMeter.unitFactor != null &&
                        locationMeter.unitFactor
                            .toString()
                            .includes(filterData.unitFactor ?? ''))) &&
                this.filterLocationMeterValuesFilterList(
                    locationMeter.meterBuildLength != null
                        ? locationMeter.meterBuildLength.toString()
                        : this.translateService.translate('common_NO_VALUE'),
                    filterData.meterBuildLength,
                ) &&
                this.filterLocationMeterValuesFilterList(
                    locationMeter.meterConnectionType != null
                        ? locationMeter.meterConnectionType.toString()
                        : this.translateService.translate('common_NO_VALUE'),
                    filterData.meterConnectionType,
                ) &&
                this.filterLocationMeterValuesFilterList(
                    locationMeter.shutoffValve != null
                        ? locationMeter.shutoffValve === 'A'
                            ? this.translateService.translate('utilities_.AFTER')
                            : locationMeter.shutoffValve === 'B'
                              ? this.translateService.translate('utilities_.BEFORE')
                              : locationMeter.shutoffValve === 'M'
                                ? this.translateService.translate('utilities_.BOTH')
                                : locationMeter.shutoffValve === 'N'
                                  ? this.translateService.translate('utilities_.NONE')
                                  : this.translateService.translate('common_NO_VALUE')
                        : this.translateService.translate('common_NO_VALUE'),
                    filterData.shutoffValve,
                ) &&
                this.filterLocationMeterValuesFilterList(
                    locationMeter.sensorPocket != null && locationMeter.sensorPocket
                        ? this.translateService.translate('actions_yes', 'online', 'common')
                        : this.translateService.translate('actions_no', 'online', 'common'),
                    filterData.sensorPocket,
                ) &&
                this.filterLocationMeterValuesFilterList(
                    this.translateService.translate(
                        'mappers_meterType.' + locationMeter.meterSuperTypeNo,
                        'online',
                        'common',
                    ),
                    filterData.meterSuperTypeNo,
                ) &&
                this.filterDate(locationMeter, filterData.mountingDate, 'mountingDate') &&
                this.filterLocationMeterValuesFilterList(
                    locationMeter.supplyOrReturnFlow != null
                        ? locationMeter.supplyOrReturnFlow === 'C'
                            ? this.translateService.translate('utilities_.COLD')
                            : locationMeter.supplyOrReturnFlow === 'W'
                              ? this.translateService.translate('utilities_.WARM')
                              : this.translateService.translate('common_NO_VALUE')
                        : this.translateService.translate('common_NO_VALUE'),
                    filterData.supplyOrReturnFlow,
                ) &&
                this.filterLocationMeterValuesFilterList(
                    this.translateService.translate(
                        locationMeter.radioTechnology != null
                            ? 'radiotechnology_' + locationMeter.radioTechnology
                            : 'common_NO_VALUE',
                    ),
                    filterData.radioTechnology,
                ) &&
                this.filterLocationMeterValuesFilterList(
                    locationMeter.dinRail != null && locationMeter.dinRail
                        ? this.translateService.translate('actions_yes', 'online', 'common')
                        : this.translateService.translate('actions_no', 'online', 'common'),
                    filterData.dinRail,
                ) &&
                this.filterLocationMeterValuesFilterList(
                    locationMeter.maxCurrent?.toString() ??
                        this.translateService.translate('common_NO_VALUE'),
                    filterData.maxCurrent,
                ) &&
                this.filterLocationMeterValuesFilterList(
                    locationMeter.meterSize != null
                        ? locationMeter.meterSize
                        : this.translateService.translate('common_NO_VALUE'),
                    filterData.meterSize,
                ),
        );
    }

    saveFilter(): void {
        const rawValues = this.formGroupFilter.getRawValue();
        const valuesToSave: FilterData = {
            proBraLoc: rawValues.proBraLoc,
            address: rawValues.address,
            meterSuperTypeNo:
                rawValues.meterSuperTypeNo?.map(it => ({name: it.name, selected: it.selected})) ??
                [],
            alloUnitType:
                rawValues.alloUnitType?.map(it => ({name: it.name, selected: it.selected})) ?? [],
            serialNo: rawValues.serialNo,
            meterModel: rawValues.meterModel,
            scale: rawValues.scale,
            unitFactor: rawValues.unitFactor,
            mountingDate: rawValues.mountingDate,
            roomId: rawValues.roomId,
            meterBuildLength:
                rawValues.meterBuildLength?.map(it => ({name: it.name, selected: it.selected})) ??
                [],
            meterConnectionType:
                rawValues.meterConnectionType?.map(it => ({
                    name: it.name,
                    selected: it.selected,
                })) ?? [],
            shutoffValve: rawValues.shutoffValve,
            supplyOrReturnFlow:
                rawValues.supplyOrReturnFlow?.map(it => ({name: it.name, selected: it.selected})) ??
                [],
            dinRail: rawValues.dinRail?.map(it => ({name: it.name, selected: it.selected})) ?? [],
            maxCurrent:
                rawValues.maxCurrent?.map(it => ({name: it.name, selected: it.selected})) ?? [],
            sensorPocket: rawValues.sensorPocket,
            meterSize:
                rawValues.meterSize?.map(it => ({name: it.name, selected: it.selected})) ?? [],
            meterConnectionTypeId: rawValues.meterConnectionTypeId,
            meterSequenceNo: rawValues.meterSequenceNo,
            radioTechnology:
                rawValues.radioTechnology?.map(it => ({name: it.name, selected: it.selected})) ??
                [],
        };
        this.meterService.setFilterColumns(valuesToSave);
    }

    private sortMeters(meters: LocationMeter[], sorting: Sorting): LocationMeter[] {
        switch (sorting.column) {
            case 'proBraLoc':
                return meters.sort(
                    composeComparators([
                        numericLocaleComparator(
                            meter => meter.propertyNo?.toString() ?? '',
                            sorting.direction != null ? sorting.direction : 'asc',
                        ),
                        numericLocaleComparator(
                            meter => meter.branchNo?.toString() ?? '',
                            sorting.direction != null ? sorting.direction : 'asc',
                        ),
                        numericLocaleComparator(
                            meter => meter.locationNo?.toString() ?? '',
                            sorting.direction != null ? sorting.direction : 'asc',
                        ),
                    ]),
                );
            case 'scale':
            case 'meterBuildLength':
            case 'maxCurrent':
            case 'unitFactor':
            case 'meterSequenceNo':
            case 'meterSize':
            case 'dinRail':
            case 'sensorPocket':
                return meters.sort(
                    numericLocaleComparator(
                        meter => meter[sorting.column as keyof LocationMeter]?.toString() ?? '',
                        sorting.direction != null ? sorting.direction : 'asc',
                    ),
                );
            case 'alloUnitType':
                return meters.sort(
                    comparator({
                        selector: meter =>
                            this.translateService.translate(
                                'mappers_allocationUnitMap.' + meter.alloUnitType,
                                'online',
                                'common',
                            ),
                        order: sorting.direction ?? 'asc',
                    }),
                );
            case 'meterSuperTypeNo':
                return meters.sort(
                    comparator({
                        selector: meter =>
                            this.translateService.translate(
                                'mappers_meterType.' + meter.meterSuperTypeNo,
                                'online',
                                'common',
                            ),
                        order: sorting.direction ?? 'asc',
                    }),
                );
            case 'supplyOrReturnFlow':
                return meters.sort(
                    comparator({
                        selector: meter => {
                            switch (meter.supplyOrReturnFlow) {
                                case 'C':
                                    return this.translateService.translate('utilities_.COLD');
                                case 'W':
                                    return this.translateService.translate('utilities_.WARM');
                                default:
                                    return '';
                            }
                        },
                        order: sorting.direction ?? 'asc',
                    }),
                );
            case 'shutoffValve':
                return meters.sort(
                    comparator({
                        selector: meter => {
                            switch (meter.shutoffValve) {
                                case 'B':
                                    return this.translateService.translate('utilities_.BEFORE');
                                case 'A':
                                    return this.translateService.translate('utilities_.AFTER');
                                case 'M':
                                    return this.translateService.translate('utilities_.BOTH');
                                case 'N':
                                    return this.translateService.translate('utilities_.NONE');
                                default:
                                    return '';
                            }
                        },
                        order: sorting.direction ?? 'asc',
                    }),
                );
            case 'radioTechnology':
                return meters.sort(
                    comparator({
                        selector: meter =>
                            this.translateService.translate(
                                'radiotechnology_' + meter.meterSuperTypeNo,
                            ),
                        order: sorting.direction ?? 'asc',
                    }),
                );
            default:
                return meters.sort(
                    comparator({
                        selector: meter => meter[sorting.column as keyof LocationMeter]?.toString(),
                        order: sorting.direction ?? 'asc',
                    }),
                );
        }
    }

    onSort(sorting: Sorting): void {
        applySortingToHeaders(this.headers(), sorting);
        this.meterService.updateSorting(sorting);
    }

    setPage(page: number): void {
        this.meterService.page.set(page);
    }

    setPageSize(pageSize: number): void {
        this.meterService.pageSize.set(pageSize);
    }

    resetColumns(): void {
        this.selectedColumnsForm.patchValue({
            proBraLoc: true,
            address: true,
            meterSuperTypeNo: true,
            alloUnitType: true,
            serialNo: true,
            meterModel: true,
            scale: false,
            unitFactor: false,
            mountingDate: false,
            roomId: false,
            meterBuildLength: false,
            meterConnectionType: false,
            shutoffValve: false,
            supplyOrReturnFlow: false,
            dinRail: false,
            maxCurrent: false,
            sensorPocket: false,
            meterSize: false,
            meterConnectionTypeId: false,
            meterSequenceNo: false,
            radioTechnology: false,
        });
    }

    downloadFile(buildingNo: string): void {
        this.meterService.downloadPhysicalMeterExportFile(buildingNo);
    }

    updateSelectedColumns(selectedColumns: SelectedColumns): void {
        this.meterService.setSelectedColumns(selectedColumns);
    }

    private updateFilterOptions(
        locationMeters: LocationMeter[],
        filterSubject: BehaviorSubject<FilterData | null>,
    ): void {
        if (filterSubject.value == null) {
            this.formGroupFilter.patchValue(
                {
                    meterSuperTypeNo: this.getUniqueValuesForLists(
                        locationMeters,
                        'meterSuperTypeNo',
                    ),
                    alloUnitType: this.getUniqueValuesForLists(locationMeters, 'alloUnitType'),
                    meterBuildLength: this.getUniqueValuesForLists(
                        locationMeters,
                        'meterBuildLength',
                    ),
                    meterConnectionType: this.getUniqueValuesForLists(
                        locationMeters,
                        'meterConnectionType',
                    ),
                    radioTechnology: this.getUniqueValuesForLists(
                        locationMeters,
                        'radioTechnology',
                    ),
                    meterSize: this.getUniqueValuesForLists(locationMeters, 'meterSize'),
                    maxCurrent: this.getUniqueValuesForLists(locationMeters, 'maxCurrent'),
                    sensorPocket: this.getUniqueValuesForLists(locationMeters, 'sensorPocket'),
                    supplyOrReturnFlow: this.getUniqueValuesForLists(
                        locationMeters,
                        'supplyOrReturnFlow',
                    ),
                    shutoffValve: this.getUniqueValuesForLists(locationMeters, 'shutoffValve'),
                    dinRail: this.getUniqueValuesForLists(locationMeters, 'dinRail'),
                },
                {emitEvent: false},
            );
        } else {
            this.formGroupFilter.patchValue(filterSubject.value, {emitEvent: false});
        }
    }
}
